import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { TopBar } from '@/components/TopBar';
import { ChatLandingView } from '@/components/ChatPage/ChatLandingView';
import { ChatInput, useChatInput, REPLICA_WARMUP_LABEL, VM_BOOT_LABEL } from '@/components/ChatInput';
import { ImmediateResultSlot, useImmediateResult } from '@/components/ImmediateResult';
import {
  DisconnectBanner,
  PowerOnBanner,
  AgentErrorBanner,
} from '@/components/ChatPage/StatusBanners';
import { useAppSession } from '@/contexts/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useMobileIsland } from '@/hooks/useMobileIsland';
import { useVmConnection } from '@/api/context';
import { usePaperThemeActive } from '@/hooks/useThemeFlag';
import { PaperLandingPage } from '@/components/paper/PaperLandingPage';
import { Titles } from '@/lib/navigation';

const VM_BOOT_STATUS_DELAY_MS = 1000;

export const LandingPage = () => {
  const isPaper = usePaperThemeActive();
  if (isPaper) return <PaperLandingPage />;
  return <LandingPageLegacy />;
};

const LandingPageLegacy = () => {
  useDocumentTitle(Titles.chat());
  const { session, artifacts, agentError, dismissAgentError } = useAppSession();
  const { chat, chatHistory, isPoweringOn } = session;
  const auth = useAuth();
  const mobileIsland = useMobileIsland();
  const vmConnection = useVmConnection();
  const [showVmBootStatus, setShowVmBootStatus] = useState(false);
  useEffect(() => {
    if (vmConnection.status !== 'connecting') {
      setShowVmBootStatus(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setShowVmBootStatus(true);
    }, VM_BOOT_STATUS_DELAY_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [vmConnection.status]);

  const chatInput = useChatInput({
    isRunning: false,
    startupLabel: vmConnection.status === 'ready' ? REPLICA_WARMUP_LABEL : VM_BOOT_LABEL,
    showStartupStatus: showVmBootStatus,
    sendInFlight: chat.sendInFlight,
    clearOnSubmit: false,
  });

  // Dev-only: when a `$$ …` command emits an ImmediateResult on the
  // landing page, collapse the top padding so the result gets the
  // whole screen below the input. Always false in production (the
  // block is never rendered there).
  const { current: immediateResult } = useImmediateResult();

  return (
    <main
      className={cn(
        'relative flex flex-1 flex-col overflow-y-auto transition-[padding] duration-300',
        artifacts.artifactOpen && 'desktop:pl-20',
      )}
    >
      {!auth.isAuthenticated && <TopBar className="absolute top-0 left-0 right-0 z-10" />}

      <ChatLandingView
        mobileIsland={mobileIsland}
        disconnected={chatHistory.disconnected}
        compact={!!immediateResult}
        statusBanners={<>
          <DisconnectBanner show={chatHistory.disconnected} />
          <PowerOnBanner show={isPoweringOn} />
          <AgentErrorBanner
            show={!!agentError}
            error={agentError}
            onDismiss={dismissAgentError}
          />
        </>}
        chatInput={
          <>
            <ChatInput
              {...chatInput}
              isRunning={false}
              className="pb-[max(1rem,env(safe-area-inset-bottom))] desktop:pb-0"
            />
            <ImmediateResultSlot />
          </>
        }
      />
    </main>
  );
};
